/**
 * <TopNav> — desktop navigation row. Owns its own dropdown hover-timer
 * state for the Home + Accounts mega-menus, so the parent <Layout> stays
 * stateless about navigation.
 *
 * The 120ms grace timer on mouse-leave is what stops the dropdown from
 * snapping shut while you're crossing the gap between the trigger and
 * the panel. Each menu has a tiny "hover bridge" div under the trigger
 * for the same reason.
 */
import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

// 120ms is small enough that intentional moves-away feel responsive but
// long enough to forgive a fast diagonal hover from trigger → panel.
const HOVER_GRACE_MS = 120;

const TopNav = ({ navigation, homeSubLinks, adminSubLinks = [], accounts, currentPath }) => {
  const [homeOpen, setHomeOpen] = useState(false);
  const [accountsOpen, setAccountsOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const homeTimer = useRef(null);
  const accountsTimer = useRef(null);
  const adminTimer = useRef(null);

  // Generic hover-timer helpers — same pattern reused for both menus.
  const hoverHandlers = (timerRef, setOpen) => ({
    onMouseEnter: () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setOpen(true);
    },
    onMouseLeave: () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setOpen(false), HOVER_GRACE_MS);
    },
    closeNow: () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      setOpen(false);
    },
  });

  return (
    <nav className="hidden md:flex items-center gap-1">
      {navigation.map((item) => (
        <NavItem
          key={item.name}
          item={item}
          currentPath={currentPath}
          homeSubLinks={homeSubLinks}
          adminSubLinks={adminSubLinks}
          accounts={accounts}
          homeOpen={homeOpen}
          accountsOpen={accountsOpen}
          adminOpen={adminOpen}
          homeHover={hoverHandlers(homeTimer, setHomeOpen)}
          accountsHover={hoverHandlers(accountsTimer, setAccountsOpen)}
          adminHover={hoverHandlers(adminTimer, setAdminOpen)}
        />
      ))}
    </nav>
  );
};

const NavItem = ({
  item, currentPath, homeSubLinks, adminSubLinks, accounts,
  homeOpen, accountsOpen, adminOpen, homeHover, accountsHover, adminHover,
}) => {
  const Icon = item.icon;
  const isAccounts = item.name === 'Accounts';
  const isHome = item.name === 'Home';
  const isAdmin = item.name === 'Admin';
  const homeSubActive = homeSubLinks.some((s) => currentPath === s.href);
  const adminSubActive = (adminSubLinks || []).some((s) => currentPath === s.href);
  const isActive = isAccounts
    ? (currentPath === '/accounts' || currentPath.startsWith('/accounts/'))
    : isHome
      ? (currentPath === item.href || homeSubActive)
      : isAdmin
        ? (currentPath.startsWith('/admin/') || adminSubActive)
        : currentPath === item.href;
  const baseClasses = `flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
    isActive
      ? 'bg-[#0052FF] text-white shadow-[0_0_0_1px_rgba(255,255,255,0.06)_inset]'
      : 'text-slate-300 hover:text-white hover:bg-white/10'
  }`;

  if (isHome) {
    return (
      <DropdownGroup
        item={item} Icon={Icon} baseClasses={baseClasses}
        open={homeOpen} hover={homeHover}
        groupTestId="nav-home-group" panelTestId="nav-home-dropdown"
        overviewLabel="Welcome" overviewTestId="nav-home-overview" minWidthClass="min-w-[200px]"
      >
        {homeSubLinks.map((s) => (
          <DropdownLink
            key={s.href} to={s.href} label={s.label}
            onClose={homeHover.closeNow}
            testId={`nav-home-item-${s.href.replace('/', '')}`}
          />
        ))}
      </DropdownGroup>
    );
  }

  if (isAccounts && accounts.length > 0) {
    return (
      <DropdownGroup
        item={item} Icon={Icon} baseClasses={baseClasses}
        open={accountsOpen} hover={accountsHover}
        groupTestId="nav-accounts-group" panelTestId="nav-accounts-dropdown"
        overviewLabel="Overview" overviewTestId="nav-accounts-overview"
        overviewHref="/accounts" minWidthClass="min-w-[240px]"
      >
        <div className="max-h-[60vh] overflow-y-auto">
          {accounts.map((acc) => (
            <DropdownLink
              key={acc.account_id}
              to={`/accounts/${acc.account_id}/statement`}
              label={acc.name}
              title={acc.name}
              truncate
              onClose={accountsHover.closeNow}
              testId={`nav-accounts-item-${acc.account_id}`}
            />
          ))}
        </div>
      </DropdownGroup>
    );
  }

  if (isAdmin && (adminSubLinks || []).length > 0) {
    return (
      <DropdownGroup
        item={item} Icon={Icon} baseClasses={baseClasses}
        open={adminOpen} hover={adminHover}
        groupTestId="nav-admin-group" panelTestId="nav-admin-dropdown"
        overviewLabel="Metrics" overviewTestId="nav-admin-overview"
        overviewHref="/admin/metrics" minWidthClass="min-w-[220px]"
      >
        {adminSubLinks.filter((s) => s.href !== '/admin/metrics').map((s) => (
          <DropdownLink
            key={s.href} to={s.href} label={s.label}
            onClose={adminHover.closeNow}
            testId={`nav-admin-item-${s.href.replace(/\//g, '-')}`}
          />
        ))}
      </DropdownGroup>
    );
  }

  return (
    <Link to={item.href} className={baseClasses} data-testid={`nav-${item.name.toLowerCase()}`}>
      <Icon className="w-4 h-4" />
      {item.name}
    </Link>
  );
};

const DropdownGroup = ({
  item, Icon, baseClasses, open, hover,
  groupTestId, panelTestId, overviewLabel, overviewTestId, overviewHref,
  minWidthClass, children,
}) => (
  <div
    className="relative"
    data-testid={groupTestId}
    onMouseEnter={hover.onMouseEnter}
    onMouseLeave={hover.onMouseLeave}
  >
    <Link
      to={item.href}
      className={baseClasses}
      data-testid={`nav-${item.name.toLowerCase()}`}
      onClick={hover.closeNow}
    >
      <Icon className="w-4 h-4" />
      {item.name}
      <ChevronDown className={`w-3.5 h-3.5 opacity-70 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
    </Link>
    {/* Invisible hover bridge so the menu doesn't snap shut between trigger and panel */}
    <div className="absolute left-0 right-0 top-full h-2" aria-hidden="true" />
    {open && (
      <div
        className={`absolute left-0 top-full mt-2 ${minWidthClass} bg-white rounded-lg shadow-xl ring-1 ring-black/5 py-2 z-50 animate-in fade-in duration-150`}
        data-testid={panelTestId}
      >
        <Link
          to={overviewHref || item.href}
          className="block px-4 py-2 text-sm font-semibold text-[#0F172A] hover:bg-[#F1F5F9] hover:text-[#0052FF]"
          data-testid={overviewTestId}
          onClick={hover.closeNow}
        >
          {overviewLabel}
        </Link>
        <div className="my-1 border-t border-[#E2E8F0]" />
        {children}
      </div>
    )}
  </div>
);

const DropdownLink = ({ to, label, onClose, testId, title, truncate }) => (
  <Link
    to={to}
    title={title}
    className={`block px-4 py-2 text-sm text-[#334155] hover:bg-[#F1F5F9] hover:text-[#0052FF] ${truncate ? 'truncate' : ''}`}
    data-testid={testId}
    onClick={onClose}
  >
    {label}
  </Link>
);

export default TopNav;
